import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Check, FileSignature, Pencil, Target, X } from 'lucide-react';
import { apiErrorMessage, getUnderwritingCase, putUnderwritingCase, seedUnderwritingCaseFromAgreement, getPoolSeries, type UnderwritingCase } from '../api/abfApi';
import type { ActionFeedbackState } from '../components/ActionFeedback';
import type { PoolSeries } from '../types/collateralExplorer';

/**
 * WS5 D-8 — the underwritten case (expected CPR, CDR, loss severity, target advance rate,
 * expected yield, expected WAL) beside the latest actual from the pool series. Sourced from
 * the agreement where it states a figure, otherwise keyed by the maker with a note. Shipped in
 * the data feed's facilities dataset so a consumer's Power BI can do actual-vs-underwritten.
 */

interface Props {
  facilityId: number;
  canEdit: boolean;
  onFeedback: (feedback: ActionFeedbackState) => void;
}

const Card = styled.section`
  padding: 1rem; border: 1px solid rgba(148,163,184,.16); border-radius: 12px; background: rgba(8,16,30,.8);
  h3 { margin: 0; color: #f8fafc; font-size: 15px; }
  p.sub { margin: .3rem 0 0; color: #94a3b8; font-size: 12px; }
  table { width: 100%; border-collapse: collapse; margin-top: .9rem; font-size: 12px; font-variant-numeric: tabular-nums; }
  th { text-align: left; padding: .45rem .5rem; color: #7dd3fc; font-size: 11px; font-weight: 650; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid rgba(148,163,184,.2); }
  td { padding: .45rem .5rem; border-top: 1px solid rgba(148,163,184,.1); color: #e2e8f0; }
  th.num, td.num { text-align: right; white-space: nowrap; }
  input, select { min-height: 32px; padding: .3rem .5rem; border: 1px solid rgba(148,163,184,.2); border-radius: 8px; background: #07101f; color: #e2e8f0; font-size: 12px; }
  button { display: inline-flex; align-items: center; gap: 6px; min-height: 34px; padding: .35rem .7rem; border: 1px solid rgba(148,163,184,.24); border-radius: 8px; color: #cbd5e1; background: rgba(15,23,42,.6); cursor: pointer; font: inherit; font-size: 13px; }
  button:disabled { opacity: .5; cursor: default; }
  button.primary { color: #e0f2fe; border-color: #7dd3fc; background: rgba(14,116,144,.25); }
  .muted { color: #94a3b8; }
  .worse { color: #fda4af; } .better { color: #6ee7b7; }
  .boundary { margin-top: .7rem; color: #94a3b8; font-size: 11px; line-height: 1.5; }
`;

const fmt = (v: unknown, unit: string) => v === null || v === undefined ? '—' : unit === 'YEARS' ? `${Number(v).toFixed(2)} y` : `${Number(v).toFixed(2)}%`;

/** The latest actual that answers each underwritten metric, from the pool series. */
const actualFor = (key: string, series: PoolSeries | null): number | null => {
  const p = series?.points?.[series.points.length - 1] as Record<string, unknown> | undefined;
  if (!p) return null;
  const map: Record<string, string> = { EXPECTED_CPR_PCT: 'cpr_pct', EXPECTED_CDR_PCT: 'cdr_pct', EXPECTED_YIELD_PCT: 'weighted_avg_yield_pct', EXPECTED_WAL_YEARS: 'weighted_avg_life_years' };
  if (key === 'TARGET_ADVANCE_RATE_PCT') {
    const bb = Number(p.borrowing_base), eligible = Number(p.eligible_collateral_amount);
    return bb > 0 && eligible > 0 ? (bb / eligible) * 100 : null;
  }
  const col = map[key];
  const v = col ? p[col] : null;
  return v === null || v === undefined ? null : Number(v);
};
/** Higher-is-worse for CPR, CDR and loss severity; higher-is-better for yield and advance rate; WAL is neutral. */
const toneFor = (key: string, actual: number | null, expected: number | null): '' | 'worse' | 'better' => {
  if (actual === null || expected === null) return '';
  const worseWhenHigher = ['EXPECTED_CDR_PCT', 'EXPECTED_LOSS_SEVERITY_PCT'];
  const betterWhenHigher = ['EXPECTED_YIELD_PCT', 'TARGET_ADVANCE_RATE_PCT'];
  if (worseWhenHigher.includes(key)) return actual > expected ? 'worse' : 'better';
  if (betterWhenHigher.includes(key)) return actual < expected ? 'worse' : 'better';
  return '';
};

const UnderwritingCaseCard: React.FC<Props> = ({ facilityId, canEdit, onFeedback }) => {
  const [model, setModel] = useState<UnderwritingCase | null>(null);
  const [series, setSeries] = useState<PoolSeries | null>(null);
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<Record<string, { value: string; sourceKind: string; sourceClause: string; note: string }>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [uw, ps] = await Promise.all([getUnderwritingCase(facilityId), getPoolSeries(facilityId).catch(() => null)]);
      setModel(uw);
      setSeries(ps);
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The underwriting case could not be loaded.') });
    }
  }, [facilityId, onFeedback]);
  useEffect(() => { void load(); }, [load]);

  const startEdit = () => {
    const next: typeof rows = {};
    for (const m of model?.metrics ?? []) next[m.key] = { value: m.value === null ? '' : String(m.value), sourceKind: m.sourceKind || 'MAKER', sourceClause: m.sourceClause || '', note: m.note || '' };
    setRows(next);
    setEditing(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      const metrics = Object.entries(rows).filter(([, r]) => r.value !== '').map(([key, r]) => ({ key, value: r.value, sourceKind: r.sourceKind, sourceClause: r.sourceClause || undefined, note: r.note || undefined }));
      setModel(await putUnderwritingCase(facilityId, { metrics }));
      setEditing(false);
      onFeedback({ tone: 'success', message: 'Underwriting case saved; the changed metrics took a new version.' });
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The underwriting case could not be saved.') });
    } finally {
      setBusy(false);
    }
  };
  const seed = async () => {
    setBusy(true);
    try {
      setModel(await seedUnderwritingCaseFromAgreement(facilityId));
      onFeedback({ tone: 'success', message: 'The figures the agreement states are now the underwriting case, each with its clause.' });
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'Nothing could be adopted from the agreement.') });
    } finally {
      setBusy(false);
    }
  };

  if (!model) return null;
  const set = model.metrics.filter(m => m.value !== null).length;
  return (
    <Card aria-label="Underwriting case">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div>
          <h3><Target size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />Underwritten case vs actual</h3>
          <p className="sub">{set === 0 ? 'No underwriting metric is set yet. Adopt what the agreement states, or key the IC case with a note.' : `${set} of ${model.metrics.length} metrics set; actuals are the latest released tape's pool statistics.`}</p>
        </div>
        {canEdit && !editing && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {model.agreementCandidates.length > 0 && <button type="button" disabled={busy} onClick={() => void seed()}><FileSignature size={14} />Adopt from agreement ({model.agreementCandidates.length})</button>}
          <button type="button" onClick={startEdit}><Pencil size={14} />{set === 0 ? 'Key the case' : 'Revise'}</button>
        </div>}
      </div>
      <table>
        <thead><tr><th>Metric</th><th className="num">Underwritten</th><th className="num">Latest actual</th><th>Source</th>{editing && <th>Clause / note</th>}</tr></thead>
        <tbody>
          {model.metrics.map(m => {
            const actual = actualFor(m.key, series);
            const tone = toneFor(m.key, actual, m.value);
            const r = rows[m.key];
            return <tr key={m.key}>
              <td>{m.label}</td>
              <td className="num">{editing && r ? <input aria-label={m.label} type="number" step="0.01" value={r.value} onChange={e => setRows({ ...rows, [m.key]: { ...r, value: e.target.value } })} style={{ width: 110, textAlign: 'right' }} /> : fmt(m.value, m.unit)}</td>
              <td className={`num ${tone}`}>{fmt(actual, m.unit)}</td>
              <td className="muted">{editing && r ? <select aria-label={`${m.label} source`} value={r.sourceKind} onChange={e => setRows({ ...rows, [m.key]: { ...r, sourceKind: e.target.value } })}><option value="MAKER">Maker (IC memo)</option><option value="AGREEMENT">Agreement</option></select>
                : m.value === null ? '—' : `${m.sourceKind === 'AGREEMENT' ? 'agreement' : 'maker'}${m.sourceClause ? ` · ${m.sourceClause}` : ''}${m.note ? ` · ${m.note}` : ''} · v${m.versionNo} · ${m.setBy}`}</td>
              {editing && r && <td>{r.sourceKind === 'AGREEMENT'
                ? <input aria-label={`${m.label} clause`} placeholder="Clause, e.g. Schedule 2 §1.3" value={r.sourceClause} onChange={e => setRows({ ...rows, [m.key]: { ...r, sourceClause: e.target.value } })} style={{ width: '100%' }} />
                : <input aria-label={`${m.label} note`} placeholder="Where it comes from, e.g. IC memo p.4" value={r.note} onChange={e => setRows({ ...rows, [m.key]: { ...r, note: e.target.value } })} style={{ width: '100%' }} />}</td>}
            </tr>;
          })}
        </tbody>
      </table>
      {editing && <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button type="button" className="primary" disabled={busy} onClick={() => void save()}><Check size={14} />{busy ? 'Saving…' : 'Save case'}</button>
        <button type="button" disabled={busy} onClick={() => setEditing(false)}><X size={14} />Cancel</button>
      </div>}
      <div className="boundary">{model.authorityBoundary}</div>
    </Card>
  );
};

export default UnderwritingCaseCard;
